/**
 * FeedbackButton.jsx — the bug / idea box: a small speech bubble fixed to the
 * bottom-right of every authenticated page (above the tab bar on phones).
 *
 * Tap → one-line input; Enter or the send icon submits, Esc / click-away closes.
 * The page, user, time and a short device string ride along, so an admin reading
 * it in Settings ▸ Feedback knows where "this is broken" was.
 */
import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  InputAdornment,
  Typography,
  ClickAwayListener,
  CircularProgress,
  Grow,
  Tooltip,
} from '@mui/material';
import {
  ChatBubbleOutline as FeedbackIcon,
  Close as CloseIcon,
  Send as SendIcon,
  CheckCircle as SentIcon,
} from '@mui/icons-material';
import { useMobile } from '../../hooks/useMobile';
import { useSubmitFeedback } from '../../hooks/useFeedback';
import { track } from '../../lib/telemetry';
import { TAB_HEIGHT } from './BottomTabBar';

const BUTTON = 40;
const SENT_MS = 1400;

/** "iPhone · Safari · 390px" — enough to reproduce a layout bug, nothing identifying. */
function deviceString() {
  try {
    const ua = navigator.userAgent || '';
    // iPadOS Safari reports itself as a Mac; touch points give it away.
    const touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    const os = /iPhone/.test(ua) ? 'iPhone'
      : /iPad/.test(ua) || touchMac ? 'iPad'
      : /Android/.test(ua) ? 'Android'
      : /CrOS/.test(ua) ? 'ChromeOS'
      : /Macintosh|Mac OS X/.test(ua) ? 'Mac'
      : /Windows/.test(ua) ? 'Windows'
      : /Linux/.test(ua) ? 'Linux'
      : 'Other';
    // Order matters: Edge/Opera/Samsung UAs also say Chrome, and Chrome says Safari.
    const browser = /Edg(e|A|iOS)?\//.test(ua) ? 'Edge'
      : /OPR\/|Opera/.test(ua) ? 'Opera'
      : /SamsungBrowser/.test(ua) ? 'Samsung'
      : /Firefox\/|FxiOS/.test(ua) ? 'Firefox'
      : /Chrome\/|CriOS/.test(ua) ? 'Chrome'
      : /Safari\//.test(ua) ? 'Safari'
      : 'Other';
    return `${os} · ${browser} · ${window.innerWidth}px`.slice(0, 120);
  } catch {
    return null;
  }
}

// Screens with their own fixed control in this corner: lift the bubble clear of
// it rather than hide it.
function liftFor(pathname, isMobile) {
  // QuoteBuilder's mobile summary bar (~53px, Preview button at its right end)
  // sits right on top of the tab bar. QuotesPage keeps its tab in state, so the
  // other quote tabs get the lift too — harmless.
  if (isMobile && pathname === '/quotes') return 56;
  // ProjectsPage's 56px "Log hours" Fab, 24px in from the corner.
  if (pathname === '/projects') return 72;
  // Carousel's mobile Export step: a sticky foot 72–137px up whose primary Save
  // button sits at its right end.
  if (isMobile && pathname === '/carousel') return 80;
  return 0;
}

export default function FeedbackButton() {
  const location = useLocation();
  const { isMobile } = useMobile();
  const submit = useSubmitFeedback();
  const [open, setOpen] = useState(false);
  // Kept across close/reopen so a stray click-away doesn't lose a half-typed note.
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const sentTimer = useRef(null);

  useEffect(() => () => clearTimeout(sentTimer.current), []);

  // The input is disabled while sending, which drops focus; put it back for a retry.
  useEffect(() => {
    if (error) inputRef.current?.focus();
  }, [error]);

  // Reset on open, not on close: resetting `sent` mid exit-animation would mount
  // the autofocused input again and grab focus (and the phone keyboard).
  const openBox = () => {
    setSent(false);
    setError('');
    setOpen(true);
  };

  const close = () => {
    clearTimeout(sentTimer.current);
    setOpen(false);
  };

  const send = async () => {
    const message = text.trim();
    if (!message || submit.isPending) return;
    setError('');
    try {
      await submit.mutateAsync({
        message,
        page: `${location.pathname}${location.search}`.slice(0, 300),
        device: deviceString(),
      });
      track('feedback.sent');
      setText('');
      setSent(true);
      // The input is about to unmount; keep keyboard users (and Esc) on the box
      // unless they've already clicked somewhere else.
      if (!document.activeElement || document.activeElement === document.body) buttonRef.current?.focus();
      sentTimer.current = setTimeout(close, SENT_MS);
    } catch (err) {
      // No status = the request never reached the server.
      setError(err?.status ? (err.message || "Couldn't send — try again.") : "Couldn't reach the server — try again.");
    }
  };

  const lift = liftFor(location.pathname, isMobile);

  return (
    <ClickAwayListener
      onClickAway={close}
      mouseEvent={open ? 'onClick' : false}
      touchEvent={open ? 'onTouchEnd' : false}
    >
      <Box
        onKeyDown={(e) => {
          if (e.key === 'Escape' && open) {
            e.stopPropagation();
            close();
            buttonRef.current?.focus();
          }
        }}
        sx={{
          position: 'fixed',
          display: 'flex',
          right: isMobile ? 12 : 20,
          bottom: isMobile
            ? `calc(${TAB_HEIGHT + 12 + lift}px + env(safe-area-inset-bottom, 0px))`
            : `${20 + lift}px`,
          // Above page content (tops out at z-30), below everything that overlays it:
          // the portal's Tailwind modals sit at z-50, under MUI's speedDial (1050), so
          // speedDial would float the bubble over their backdrops and corner buttons.
          zIndex: 40,
          transition: 'bottom 150ms',
          '@media print': { display: 'none' },
        }}
      >
        <Grow in={open} unmountOnExit style={{ transformOrigin: 'bottom right' }}>
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: BUTTON + 8,
              width: isMobile ? 'calc(100vw - 24px)' : 360,
              maxWidth: 'calc(100vw - 24px)',
              p: 1,
              borderRadius: 2,
            }}
          >
            {sent ? (
              <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.9 }}>
                <SentIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Thanks — sent</Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                size="small"
                autoFocus
                inputRef={inputRef}
                placeholder="Bug or idea? One line is plenty"
                value={text}
                onChange={(e) => { setText(e.target.value); if (error) setError(''); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    send();
                  }
                }}
                disabled={submit.isPending}
                error={!!error}
                helperText={error || undefined}
                slotProps={{
                  htmlInput: { maxLength: 1000, enterKeyHint: 'send', 'aria-label': 'Bug or idea' },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          data-track="feedback.send"
                          aria-label="Send"
                          onClick={send}
                          disabled={!text.trim() || submit.isPending}
                          sx={{ color: 'primary.main' }}
                        >
                          {submit.isPending ? <CircularProgress size={16} /> : <SendIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          </Paper>
        </Grow>
        {/* Always wrapped (empty title = no tooltip) so the button never remounts and Esc can refocus it. */}
        <Tooltip title={isMobile || open ? '' : 'Bug or idea?'} placement="left">
          <IconButton
            ref={buttonRef}
            data-track={open ? 'feedback.close' : 'feedback.open'}
            aria-label={open ? 'Close feedback' : 'Report a bug or idea'}
            aria-expanded={open}
            onClick={() => (open ? close() : openBox())}
            sx={{
              width: BUTTON,
              height: BUTTON,
              bgcolor: 'background.paper',
              color: open ? 'primary.main' : 'text.secondary',
              border: '1px solid',
              borderColor: open ? 'primary.main' : 'divider',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              '&:hover': { bgcolor: 'background.paper', color: 'primary.main', borderColor: 'primary.main' },
            }}
          >
            {open ? <CloseIcon sx={{ fontSize: 18 }} /> : <FeedbackIcon sx={{ fontSize: 19 }} />}
          </IconButton>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
}
